import React, { useEffect, useState } from 'react';
import { database } from '../firebaseConfig';
import { ref, onValue } from 'firebase/database';
import { agentSetupModule, allSteps } from '../data/agentSetupTraining';

const FONT = 'Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';
const styles = {
  panel: { backgroundColor: '#ffffff', border: '1px solid #d8c9a8', borderRadius: '10px', padding: '28px', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' },
  back: { background: 'none', border: 'none', color: '#8C6D1F', cursor: 'pointer', padding: 0, fontSize: '14px', marginBottom: '12px', fontFamily: FONT },
  title: { fontSize: '20px', fontWeight: 700, margin: '0 0 4px', color: '#1a1a1a', fontFamily: FONT },
  muted: { fontSize: '14px', color: '#7f7f7f', margin: '0 0 16px', fontFamily: FONT },
  trackOuter: { height: 10, background: '#efe6d2', borderRadius: 6, overflow: 'hidden', marginBottom: 6 },
  trackFill: { height: '100%', background: 'linear-gradient(90deg, #b98d2e, #e8cc7a)' },
  sectionTitle: { fontSize: '12px', fontWeight: 700, color: '#8C6D1F', letterSpacing: '0.12em', textTransform: 'uppercase', margin: '22px 0 8px', borderBottom: '1px solid #efe6d2', paddingBottom: '6px' },
  row: { display: 'flex', justifyContent: 'space-between', gap: '12px', padding: '8px 0', borderBottom: '1px solid #f5efe1', fontSize: '14px', color: '#1a1a1a', fontFamily: FONT },
  done: { color: '#2e7d32', fontWeight: 600, whiteSpace: 'nowrap' },
  todo: { color: '#9a9a9a', whiteSpace: 'nowrap' },
  error: { color: '#b00020', fontSize: '14px' },
};

const fmt = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return isNaN(d) ? '' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

// Read-only view of one user's Agent Setup progress. Admins only.
export default function TrainingProgress({ uid, name, onBack }) {
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const unsubscribe = onValue(
      ref(database, `users/${uid}/training/${agentSetupModule.id}`),
      (snap) => setProgress(snap.val() || {}),
      (err) => {
        console.error('Could not load training progress:', err);
        setError('Could not load training progress.');
        setProgress({});
      }
    );
    return () => unsubscribe();
  }, [uid]);

  const total = allSteps.length;
  const done = progress ? allSteps.filter((s) => progress[s.id]).length : 0;
  const percent = total ? Math.round((done / total) * 100) : 0;
  const nextStep = progress ? allSteps.find((s) => !progress[s.id]) : null;

  return (
    <div style={styles.panel}>
      <button type="button" onClick={onBack} style={styles.back}>← Back to users</button>
      <h2 style={styles.title}>{name}: Agent Onboarding</h2>
      <p style={styles.muted}>
        {progress === null
          ? 'Loading progress...'
          : `${done} of ${total} steps complete (${percent}%)${nextStep ? ` · Next: ${nextStep.title}` : ' · All steps complete'}`}
      </p>
      <div style={styles.trackOuter}><div style={{ ...styles.trackFill, width: `${percent}%` }} /></div>
      {error && <p style={styles.error}>{error}</p>}

      {progress !== null && agentSetupModule.sections.map((section) => (
        <div key={section.id}>
          <div style={styles.sectionTitle}>{section.title}</div>
          {section.steps.map((s) => {
            const entry = progress[s.id];
            return (
              <div key={s.id} style={styles.row}>
                <span>{s.title}</span>
                {entry ? (
                  <span style={styles.done}>✓ Completed {fmt(entry.completedAt)}</span>
                ) : (
                  <span style={styles.todo}>Not started</span>
                )}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}
